import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	computed,
	inject,
	signal,
	TemplateRef,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import {
	Router,
	RouterLink,
	RouterLinkActive,
	RouterOutlet,
} from '@angular/router';
import { APP_VERSION, DialogService } from '@context/frontend/common';
import { getUaDevice } from '@context/frontend/native/platform';
import { CreateOrganizationDialogComponent } from '@context/frontend/organization';
import { DetailMenuDropdownComponent } from '@context/frontend/ui/components';
import {
	AvatarComponent,
	LogoutDialogComponent,
	SuperAuthService,
	UserNamePipe,
} from '@context/frontend/user';
import { DetailMenuItem } from '@context/shared/types/common';
import { Organization } from '@context/shared/types/organization';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiTabBar } from '@taiga-ui/addon-mobile';
import {
	TuiButton,
	TuiDialogContext,
	TuiDropdown,
	TuiIcon,
	TuiLink,
} from '@taiga-ui/core';
import { TuiBadgedContent } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { Subscription } from 'rxjs';
import { MobileMenuDialogComponent } from '../../dialogs';
import { MenuItem } from '../navigation-template/navigation.const';
import {
	AdminEntityMenuItems,
	AdminEntityType,
	AdminMenuItems,
	AdminMenuKey,
} from './admin-navigation.const';

@Component({
	imports: [
		RouterOutlet,
		TranslocoModule,
		RouterLink,
		RouterLinkActive,
		TuiButton,
		TuiIcon,
		TuiDropdown,
		UserNamePipe,
		AvatarComponent,
		DetailMenuDropdownComponent,
		TuiLink,
		TuiBadgedContent,
		AsyncPipe,
		NgTemplateOutlet,
		TuiTabBar,
	],
	selector: 'ctx-admin-navigation-template',
	templateUrl: 'admin-navigation-template.component.html',
	styleUrl: 'admin-navigation-template.component.scss',
	encapsulation: ViewEncapsulation.None,
	host: {
		'[attr.device]': 'device',
		'[class.collapsed]': 'sidebarCollapsed()',
	},
})
export class AdminNavigationTemplateComponent {
	readonly device = getUaDevice();
	readonly sidebarCollapsed = signal(false);

	readonly accountDropdown =
		viewChild<TemplateRef<unknown>>('accountDropdown');

	readonly createTemplate =
		viewChild<TemplateRef<TuiDialogContext>>('createTemplate');

	readonly accountMenuOpen = signal(false);

	readonly superAuth = inject(SuperAuthService);
	private readonly router = inject(Router);
	private readonly dialog = inject(DialogService);
	readonly version = inject(APP_VERSION);

	readonly entityTypes = AdminEntityMenuItems;

	readonly sidebarMenuItems = AdminMenuItems();

	readonly logoutDialog = this.dialog.open<boolean>(
		new PolymorpheusComponent(LogoutDialogComponent),
		LogoutDialogComponent.Options(),
	);

	readonly mobileMenuDialog = (menuItems: MenuItem<AdminMenuKey>[]) =>
		this.dialog.open(
			new PolymorpheusComponent(MobileMenuDialogComponent),
			MobileMenuDialogComponent.Options({ data: { menuItems } }),
		);

	readonly authUser = computed(() => this.superAuth.authUser());

	/** Account menu items for the dropdown. */
	readonly accountMenuItems = computed<DetailMenuItem[]>(() => {
		const items: DetailMenuItem[] = [];

		// Sign out is always available
		items.push({
			text: 'navigation.sign-out',
			iconStart: '@tui.log-out',
			handler: () => this.onPromptLogout(),
		});

		return items;
	});

	createDialogSubscription: Subscription | null = null;

	readonly onToggleSidebar = () => this.sidebarCollapsed.update((v) => !v);

	readonly onPromptLogout = () => {
		this.accountMenuOpen.set(false);
		this.logoutDialog.subscribe({
			next: (data) => this.onDismissLogout(data),
		});
	};

	readonly onMenuOpen = () =>
		this.mobileMenuDialog(this.sidebarMenuItems).subscribe();

	readonly onDismissLogout = (logout: boolean) =>
		logout ? this.router.navigate(['/auth/logout']) : {};

	onPromptCreate() {
		const template = this.createTemplate();
		if (!template) return;
		this.createDialogSubscription = this.dialog
			.open(template, {
				label: 'navigation.create',
				size: 's',
			})
			.subscribe({
				complete: () => {
					this.createDialogSubscription = null;
				},
			});
	}

	onCreate(entityType: AdminEntityType) {
		this.createDialogSubscription?.unsubscribe();
		switch (entityType) {
			case 'organization':
				this.dialog
					.open<Partial<Organization> | null>(
						new PolymorpheusComponent(
							CreateOrganizationDialogComponent,
						),
						CreateOrganizationDialogComponent.Options(),
					)
					.subscribe({
						next: (result) => {
							if (!result || !result.id) return;
							this.router.navigate(['organizations', result.id]);
						},
					});
				break;
		}
	}
}
